import React, { useState } from 'react';
import { useDataProvider, useGetIdentity, useNotify, useRefresh } from 'react-admin';
import { Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { CustomerEventType, Funeral } from '../types';
import { MAX_BELONGINGS, belongingsLabel, normalizeBelongings } from './dropoffLedger';

/**
 * What belongings came in with the deceased (T-143). The drop-off facility
 * records it in its portal; staff can correct it here. Same column, so the
 * portal shows staff's edit, and the case feed gets a note saying who changed it.
 */
export function BelongingsEdit({ record }: { record: Funeral }) {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const stored = record.dropoff_belongings ?? null;

  const start = () => {
    setDraft(stored ?? '');
    setEditing(true);
  };

  const save = async () => {
    const next = normalizeBelongings(draft);
    if (next === stored) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await dataProvider.update('funerals', {
        id: record.id,
        data: { dropoff_belongings: next },
        previousData: record,
      });
      const staff = identity?.fullName || 'Cleo staff';
      // Best-effort: the edit already landed, a missing feed line shouldn't undo it.
      await dataProvider
        .create('customer_events', {
          data: {
            funeral_id: Number(record.id),
            type: CustomerEventType.INTERNAL_NOTE,
            timestamp: new Date().toISOString(),
            user_name: staff,
            message: next ? `Belongings updated by ${staff}: ${next}` : `Belongings cleared by ${staff}`,
          },
        })
        .catch((e) => console.error('Error logging belongings note:', e));
      notify(next ? 'Belongings saved' : 'Belongings cleared', { type: 'success' });
      setEditing(false);
      refresh();
    } catch (e) {
      console.error('Error updating belongings:', e);
      notify('Could not save the belongings', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex justify-between items-start gap-3 py-1.5">
        <span className="text-[13px] text-muted-foreground leading-5 shrink-0">Belongings</span>
        <button
          type="button"
          onClick={start}
          title="Edit belongings"
          data-testid="belongings-edit"
          className="group inline-flex min-w-0 items-start gap-1 rounded px-1 -mr-1 text-right text-[13px] leading-5 text-foreground hover:bg-accent transition-colors"
        >
          <span className="break-words min-w-0">{belongingsLabel(record)}</span>
          <Pencil className="w-3 h-3 mt-1 shrink-0 opacity-40 group-hover:opacity-80" />
        </button>
      </div>
    );
  }

  return (
    <div className="w-full space-y-1.5 py-1.5">
      <div className="text-[13px] text-muted-foreground leading-5">Belongings</div>
      <Textarea
        autoFocus
        value={draft}
        maxLength={MAX_BELONGINGS}
        placeholder="None"
        aria-label="Belongings"
        className="min-h-[60px] text-[13px]"
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setEditing(false);
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void save();
        }}
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-muted-foreground">Empty = none · shows in the portal</span>
        <div className="flex gap-1.5">
          <Button size="sm" variant="ghost" disabled={saving} onClick={() => setEditing(false)}>
            Cancel
          </Button>
          <Button size="sm" disabled={saving} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
